import React, { useState, useEffect } from 'react';
import { Bot, Plus, X, AlertCircle, Info } from 'lucide-react';
import { Agent, AgentTool } from '../../types';
import CustomSelect from '../common/CustomSelect';

interface AgentToolSelectorProps {
  availableAgents: Agent[];
  currentAgentId?: number;
  existingToolAgentIds?: number[];
  onAgentSelected: (agent: Agent, toolName: string, description: string) => void;
  onClose: () => void;
  onCreateToolAgent?: () => void;
  autoSelectAgentId?: number | null;
}

const AgentToolSelector: React.FC<AgentToolSelectorProps> = ({
  availableAgents,
  currentAgentId,
  existingToolAgentIds = [],
  onAgentSelected,
  onClose,
  onCreateToolAgent,
  autoSelectAgentId
}) => {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [toolName, setToolName] = useState('');
  const [toolDescription, setToolDescription] = useState('');

  // Auto-select the agent if autoSelectAgentId is provided
  useEffect(() => {
    if (autoSelectAgentId) {
      const agent = availableAgents.find(a => a.agent_id === autoSelectAgentId);
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [autoSelectAgentId, availableAgents]);

  // Filter agents: exclude current agent and agents already added as tools, then sort by name
  const filteredAgents = availableAgents
    .filter(agent => agent.agent_id !== currentAgentId && !existingToolAgentIds.includes(agent.agent_id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const getAgentTypeColor = (type: string) => {
    const colors = {
      generator: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200',
      validator: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200',
      extender: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200',
      tool: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700';
  };

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    // Auto-generate tool name and description
    setToolName(`${agent.name.toLowerCase().replace(/\s+/g, '_')}_tool`);
    setToolDescription(`Use ${agent.name} to perform specialized tasks`);
  };

  const handleSubmit = () => {
    if (selectedAgent && toolName.trim() && toolDescription.trim()) {
      onAgentSelected(selectedAgent, toolName.trim(), toolDescription.trim());
      onClose();
    }
  };

  const isFormValid = selectedAgent && toolName.trim() && toolDescription.trim();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-modal-active="true">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Add Tool Agent</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select a tool agent to use as a tool in your current agent
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <button
            type="button"
            onClick={onCreateToolAgent}
            className="mb-4 flex items-center space-x-2 px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Tool Agent</span>
          </button>
          {/* Info Box */}
          <div className="flex items-start space-x-2 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg mb-4">
            <Info className="w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium">How it works:</p>
              <p>Any agent can be used as a tool. When you add an agent as a tool, your current agent can call that agent's capabilities when needed. Make sure your agent's instructions explain when to use each tool.</p>
            </div>
          </div>

          {/* Agent Selection Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Tool Agent
            </label>
            {filteredAgents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white">
                <Bot className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No tool agents available</p>
                {onCreateToolAgent && (
                  <button
                    type="button"
                    onClick={onCreateToolAgent}
                    className="mt-3 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Tool Agent
                  </button>
                )}
              </div>
            ) : (
              <CustomSelect
                value={selectedAgent?.agent_id ? String(selectedAgent.agent_id) : ''}
                onChange={(value) => {
                  const agent = filteredAgents.find(a => a.agent_id === parseInt(value));
                  if (agent) {
                    handleAgentSelect(agent);
                  }
                }}
                options={filteredAgents.map((agent) => ({
                  value: String(agent.agent_id),
                  label: agent.name,
                  description: agent.instructions.substring(0, 100) + (agent.instructions.length > 100 ? '...' : ''),
                }))}
                placeholder="Select a tool agent..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            )}
          </div>

          {/* Tool Configuration */}
          {selectedAgent && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex items-center space-x-2">
                <Bot className="w-5 h-5 text-primary-500" />
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Configure Tool</h4>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tool Name
                  </label>
                  <input
                    type="text"
                    value={toolName}
                    onChange={(e) => setToolName(e.target.value)}
                    placeholder="e.g., translate_to_spanish"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This name will be used when calling the tool
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tool Description
                  </label>
                  <textarea
                    value={toolDescription}
                    onChange={(e) => setToolDescription(e.target.value)}
                    placeholder="Describe what this tool does..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This description helps the AI understand when to use this tool
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mt-4">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-300">
              <p className="font-medium">Important:</p>
              <p>Make sure your agent's instructions explain when and how to use this tool. The tool will only be called if the AI determines it's appropriate.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            data-tutorial="btn-submit-tool-agent"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Tool
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentToolSelector; 